import { ExtraEquipmentAmenityType, extraEquipmentAmenityTypes } from "@instacover/helpers"
import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Trans } from "react-i18next"

import { AmenitiesForm } from "@/components/amenities/AmenitiesForm"
import ArrowButton from "@/components/base/ArrowButton"
import { Spinner } from "@/components/Spinner"
import { db, getNextAmenity } from "@/database/database"
import { useOrderedSteps } from "@/database/hooks"
import { amenityFormSearchSchema } from "@/helpers/zodSchemes"
import { useGetSessionDetail } from "@/hooks/api/useGetSessionDetail"
import { useSetAmenities } from "@/hooks/api/useSetAmenities"
import { useStepsRouting } from "@/hooks/useStepsRouting"
import { BaseLayout } from "@/sections/layouts/BaseLayout"
import { isExtraEquipmentAmenity } from "@/types/amenityTypes"
import { logger } from "@/utils/logging"

export const Route = createFileRoute("/$sessionId/_session/_steps/extraEquipment/")({
  component: ExtraEquipmentPage,
  validateSearch: (search) => amenityFormSearchSchema.parse(search),
})

function ExtraEquipmentPage() {
  const { sessionId } = Route.useParams()
  const { viewMode } = Route.useSearch()
  const navigate = useNavigate()

  /* Navigation to next step */
  const { firstUnfinishedStepLink } = useStepsRouting(sessionId)
  const { data: orderedSteps } = useOrderedSteps(sessionId)
  const extraEquipmentStep = useMemo(() => orderedSteps?.find((step) => step.name === "ExtraEquipment"), [orderedSteps])

  useEffect(() => {
    if (!extraEquipmentStep || !firstUnfinishedStepLink) return

    if (extraEquipmentStep.isFinished) {
      navigate(firstUnfinishedStepLink)
    }
  }, [navigate, firstUnfinishedStepLink, extraEquipmentStep])

  /* Navigation to next amenity in current step */
  useEffect(() => {
    // If there is amenity without photo and not skipped, navigate to it
    async function navigateToNextAmenity() {
      const nextAmenity = await getNextAmenity<ExtraEquipmentAmenityType>(sessionId, "ExtraEquipment")
      if (nextAmenity) {
        navigate({
          to: "/$sessionId/extraEquipment/$amenityType",
          params: { sessionId, amenityType: nextAmenity.type },
        })
      }
    }

    navigateToNextAmenity()
  }, [navigate, sessionId])

  /* Amenities */
  const { data: sessionDetail, isLoading: isLoadingSessionDetail } = useGetSessionDetail(sessionId)

  const initialAmenities = useMemo(() => {
    if (!sessionDetail?.amenities) return []

    return sessionDetail.amenities
      .filter(isExtraEquipmentAmenity)
      .filter((a) => !a.deletedAt)
      .map((a) => a.type)
  }, [sessionDetail])

  if (isLoadingSessionDetail || !sessionDetail) {
    return <BaseLayout descriptionNode={<Spinner position="center" />} />
  }

  return (
    <ExtraEquipmentForm
      initialAmenities={initialAmenities}
      sessionId={sessionId}
      viewMode={viewMode === "add" ? "add" : "create"}
    />
  )
}

type ExtraEquipmentFormProps = {
  initialAmenities: ExtraEquipmentAmenityType[]
  viewMode: "create" | "add"
  sessionId: string
}

function ExtraEquipmentForm(props: ExtraEquipmentFormProps) {
  const { initialAmenities, viewMode, sessionId } = props

  const navigate = useNavigate()

  const onSkip = useCallback(async () => {
    await db.orderedStep.update({ name: "ExtraEquipment", instahomeSessionId: sessionId, data: { isFinished: true } })
  }, [sessionId])

  /* Amenity selection state */
  const [selectedAmenities, setSelectedAmenities] = useState<ExtraEquipmentAmenityType[]>(initialAmenities)

  const handleToggleAmenity = (amenity: ExtraEquipmentAmenityType) => {
    setSelectedAmenities((prev) => {
      if (prev.includes(amenity)) {
        return prev.filter((a) => a !== amenity)
      }

      return [...prev, amenity]
    })
  }

  const { mutateAsync: setAmenitiesAsync } = useSetAmenities()

  const amenitiesOptions = useMemo(() => {
    if (viewMode === "add") {
      const notSelectedAmenities = extraEquipmentAmenityTypes.filter(
        (amenityType) => !initialAmenities.includes(amenityType),
      )

      return notSelectedAmenities
    }

    return extraEquipmentAmenityTypes
  }, [initialAmenities, viewMode])

  async function onContinue() {
    const res = await setAmenitiesAsync({
      sessionId,
      amenityTypes: selectedAmenities,
      amenityGroup: "ExtraEquipment",
      mode: viewMode,
    })

    await db.amenity.replace(res.amenities)

    const nextAmenity = await getNextAmenity<ExtraEquipmentAmenityType>(sessionId, "ExtraEquipment")
    if (!nextAmenity) {
      logger.error("No next amenity found after creating amenities")
      return
    }

    navigate({ to: "/$sessionId/extraEquipment/$amenityType", params: { sessionId, amenityType: nextAmenity.type } })
  }

  const onBackToOverview = useCallback(async () => {
    await db.orderedStep.update({ name: "ExtraEquipment", instahomeSessionId: sessionId, data: { isFinished: true } })
    navigate({ to: "/$sessionId/overview", params: { sessionId }, replace: true })
  }, [navigate, sessionId])

  return (
    <BaseLayout
      descriptionNode={
        <div>
          <div className="mb-9">
            <Heading as="h1" variant="h1" className="mb-5">
              <Trans i18nKey="extraEquipment.form.heading" />
            </Heading>

            <Text>
              <Trans
                i18nKey="extraEquipment.form.description"
                components={{
                  span: <span className="text-ic-primary font-bold uppercase" />,
                }}
              />
            </Text>
          </div>

          <AmenitiesForm options={amenitiesOptions} selectedValues={selectedAmenities} onToggle={handleToggleAmenity} />
        </div>
      }
      buttons={
        <div className="flex w-full justify-center gap-4">
          {viewMode === "create" && (
            <Button type="button" variant="secondary" onClick={onSkip} data-testid="amenity-form-skip-btn">
              <Trans i18nKey="general.skip" />
            </Button>
          )}

          {viewMode === "add" && (
            <Button type="button" variant="secondary" onClick={onBackToOverview} data-testid="amenity-form-back-btn">
              <Trans i18nKey="general.back" />
            </Button>
          )}

          <ArrowButton
            variant="primary"
            shape="wide"
            disabled={selectedAmenities.length === initialAmenities.length}
            onClick={onContinue}
            data-testid="amenity-form-continue-btn"
          />
        </div>
      }
    />
  )
}
